<script lang="ts" setup>
/* eslint-disable vue/no-v-html */

import type { SrcSetSizes } from '~/composables/useSrcSet'

export interface PagebuilderBannerProps {
    data: {
        banner: Banner
    }
}
const props = defineProps<PagebuilderBannerProps>()
const { $formatTableHtml } = useNuxtApp()
const imageDesktopSrc = computed((): SrcSetSizes | undefined =>
    useSrcSetSizes(props.data.banner.image?.path)
)
const imageMobileSrc = computed((): SrcSetSizes | undefined =>
    useSrcSetSizes(props.data.banner.image_mobile?.path || props.data.banner.image?.path)
)

const bannerBackgroundColor = computed((): { backgroundColor: string } => ({
    backgroundColor: props.data.banner.background_color,
}))

</script>

<template>
  <div class="w-100">
    <aside
      class="rounded overflow-hidden"
      data-cy="banner"
    >
      <Tracker
        :data="{
          position: props.data.banner.title,
          title: props.data.banner.cta?.text.trim(),
        }"
      >
        <div class="row g-0">
          <div
            class="col-12 col-lg-5 text-center text-lg-start pt-6 px-4 p-lg-10 mb-5 mb-lg-0 position-relative"
            :style="bannerBackgroundColor"
          >
            <slot name="tag" />
            <Heading
              level="h2"
              class="mt-4 mt-lg-3 mb-lg-4 beta"
              :class="props.data.banner.background_color === '#000000' && 'text-white'"
            >
              {{ props.data.banner.title }}
            </Heading> 
            <Typo v-if="props.data.banner.description">
              <div
                class="fs-bigger fw-bolder mb-6 mx-auto" 
                :class="props.data.banner.background_color === '#000000' ? 'text-white' : 'text-black'"
                v-html="$formatTableHtml(props.data.banner.description)"
              />
            </Typo>
            <div
              class="d-flex justify-content-center justify-content-lg-start"
            >
              <VButton
                v-if="props.data.banner.cta?.text && props.data.banner.cta?.link"
                :to="props.data.banner.cta.link"
                :target="
                  props.data.banner.cta.isExternal ? '_blank' : '_self'
                "
                :label="props.data.banner.cta.link"
                size="large"
                :color="props.data.banner.cta.color"
                class="fs-small fs-lg-normal fw-bolder"
              >
                {{ props.data.banner.cta.text }}
              </VButton>
            </div>

            <div
              class="banner-background z-index-5"
              :style="bannerBackgroundColor"
            />
          </div>
          <div class="col-12 col-lg-7 flex-shrink-0 z-index-10">
            <img
              v-if="imageMobileSrc?.srcSet"
              width="280"
              height="276"
              :srcset="imageMobileSrc?.srcSet"
              sizes="100vw"
              loading="lazy"
              decoding="sync"
              alt=""
              class="banner-image w-100 h-100 img-fluid d-lg-none"
            >
            <img
              v-if="imageDesktopSrc?.srcSet"
              width="750"
              height="300"
              :srcset="imageDesktopSrc?.srcSet"
              sizes="(min-width: 992px) 75vw, 69rem"
              loading="lazy"
              decoding="sync"
              :alt="props.data.banner.title"
              class="banner-image h-100 d-none d-lg-block position-relative"
            >
          </div>
        </div>
      </Tracker>
    </aside>
  </div>
</template>
